'use client';

import { useTranslations } from 'next-intl';
import { Typography } from '@/shared/ui/shadcn/typography';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';

/**
 * Inline upsell shown when a per-building cap (angles library, references) is
 * reached: "• Get a Facade Kit to add more", where the brand name is a teal
 * clickable link opening the pack sheet. The Facade Kit is what lifts those
 * caps on a building (`EntitlementFeatureMap::projectPackOverlay`).
 *
 * It used to open the Tokens Pro Pass paywall; that pass went off sale in
 * 2026-08, so the link would have opened a sheet with nothing to buy.
 *
 * The linked word is the brand name, identical in every locale (same rule as
 * the server's `pack_products.name`); the sentence around it is localized.
 * Mirrors Apple's / Android's `UpgradePlanText`.
 */
export function UpgradePlanText() {
  const t = useTranslations('common');
  const prefix = t('upgradeForMore.prefix');
  const brandName = t('upgradeForMore.plan');
  const suffix = t('upgradeForMore.suffix');
  const packRequiredPayload = usePaywallPayload('PACK_REQUIRED');
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const showPackPaywall = useLimitPaywallStore((s) => s.showPackPaywall);

  // The link is ALWAYS live: with the cached preflight payload it opens the
  // usual server-driven sheet, without it the payload-free entry opens the same
  // body on GET /store copy. Parity with Apple/Android, whose link opens the
  // pack sheet unconditionally — a visible upsell that does nothing when tapped
  // is worse than no upsell.
  const openPack = () => {
    if (packRequiredPayload) {
      showLimitPaywall(packRequiredPayload, 'PACK_REQUIRED');
    } else {
      showPackPaywall();
    }
  };

  return (
    <Typography variant="small" className="whitespace-nowrap">
      •{' '}
      {prefix}
      <button
        type="button"
        onClick={openPack}
        className="text-primary hover:underline"
      >
        {brandName}
      </button>
      {suffix}
    </Typography>
  );
}
